import { createApi } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/redux/store';
import {
  CreateFakeDeviceArgs,
  DeviceReportingDevice,
  EndOfLife,
  EndOfLifeDto,
  EndOfLifeListResponse,
} from '@/types/devices';

import { TagTypes } from '@/redux/slices/apiSlice';
import { baseQueryCamelized } from '@/redux/slices/helpers/baseQueryCamelized';

const transformDeviceResponse = (device: DeviceReportingDevice) => {
  return {
    id: device.id,
    organization_id: device.organization_id,
    serial_number: device.serial_number,
    hardware_model: device.hardware_model,
    hostname: device.hostname,
    os_type: device.os_type,
    os_version: device.os_version,
    os_name: device.os_name,
    ram_capacity: device.ram_capacity,
    storage_capacity: device.storage_capacity,
    storage_available: device.storage_available,
    storage_usage_percent: device.storage_usage_percent,
    filevault: device.filevault,
    filevault_status: device.filevault_status,
    filevault_encryption_percent: device.filevault_encryption_percent,
    bitlocker: device.bitlocker,
    bitlocker_status: device.bitlocker_status,
    bitlocker_encryption_percent: device.bitlocker_encryption_percent,
    encryption: device.encryption,
    encryption_percent: device.encryption_percent,
    battery_health: device.battery_health,
    firewall: device.firewall,
    gatekeeper: device.gatekeeper,
    issues: device.issues,
    created_at: device.created_at,
    updated_at: device.updated_at,
    last_osquery_enrollment: device.last_osquery_enrollment,
    last_osquery_report: device.last_osquery_report,
  };
};

/**
 * Defining a new API because devices come from a new baseUrl.
 */
export const deviceReportingSlice = createApi({
  reducerPath: 'deviceReportingSlice',
  baseQuery: baseQueryCamelized(
    {
      baseUrl: window._env_?.VITE_DEVICE_BASE_URL,
      prepareHeaders: (headers, api) => {
        const accessToken = (api.getState() as RootState).loggedUser
          ?.accessToken;

        if (accessToken) {
          headers.set('authorization', `Bearer ${accessToken}`);
        }

        return headers;
      },
    },
    { shouldCamelizeResponse: false }
  ),
  endpoints: builder => ({
    createFakeDevice: builder.mutation<
      DeviceReportingDevice,
      CreateFakeDeviceArgs
    >({
      query: ({
        organizationId,
        deviceId,
        serialNumber,
        osType,
        hardwareModel,
        issues,
      }) => ({
        url: `organizations/${organizationId}/devices/${deviceId}/fake-device`,
        method: 'POST',
        body: {
          serial_number: serialNumber,
          os_type: osType,
          hardware_model: hardwareModel,
          issues,
        },
      }),
      transformResponse: transformDeviceResponse,
      invalidatesTags: [TagTypes.Asset],
    }),
    getOSEndOfLife: builder.query<
      EndOfLifeListResponse<EndOfLife>,
      { organizationId: string; product?: 'windows' | 'macos' }
    >({
      // Use `params` so undefined doesn’t get serialized
      query: ({ organizationId, product }) => ({
        url: `organizations/${organizationId}/os/end-of-life`,
        params: product ? { product } : undefined,
      }),
      transformResponse: (
        response: EndOfLifeListResponse<EndOfLifeDto>
      ): EndOfLifeListResponse<EndOfLife> => {
        return {
          results: response.results.map(item => ({
            ...item,
            eolDate: item.eol_date,
          })) as EndOfLife[],
          total: response.total,
        };
      },
      providesTags: [TagTypes.OSEndOfLife],
    }),
  }),
  tagTypes: [TagTypes.Asset, TagTypes.OSEndOfLife],
});

// export auto-generated hooks
export const { useCreateFakeDeviceMutation, useGetOSEndOfLifeQuery } =
  deviceReportingSlice;
